import {
  Anchor,
  AppShell,
  Badge,
  Button,
  Flex,
  Group,
  NavLink,
  Stack,
  Text,
  TextProps,
} from '@mantine/core';
import { googleLogout } from '@react-oauth/google';
import {
  Fragment,
  PropsWithChildren,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { PiCaretRightLight } from 'react-icons/pi';
import { SlLogout } from 'react-icons/sl';
import { Link, useLocation } from 'wouter';
import { useBreadcrumbs } from '@/lib/breadcrumbs';
import {
  listMemoryVersion,
  listUrl,
  subscribeListMemory,
} from '@/lib/listMemory';
import { type MenuItem, menuGroups } from '@/lib/routeConfig';
import { useAuth } from '@/models/auth';
import {
  type NavCounterKey,
  type TNavCounters,
  useNavCounters,
} from '@/models/nav-counters';
import { usePermissions } from '@/models/permissions';
import Logo from './Logo';
import ThemeSwitcher from './ThemeSwitcher';

const borderLeftStyle = {
  borderLeft:
    'light-dark(1px solid #ccc, 1px solid var(--mantine-color-dark-4))',
};

function HeaderText({ children, ...props }: PropsWithChildren & TextProps) {
  return (
    <Text
      size="lg"
      style={borderLeftStyle}
      c="gray.7"
      pl="lg"
      ml="sm"
      {...props}
    >
      {children}
    </Text>
  );
}

function BreadBar() {
  const { breadcrumbs } = useBreadcrumbs();

  if (!breadcrumbs.length) {
    return null;
  }

  return (
    <Group gap={0} style={borderLeftStyle} pl="md">
      {breadcrumbs.map(({ title, to }, i) => (
        <Fragment key={to}>
          {i > 0 && (
            <PiCaretRightLight fill="light-dark(rgba(0,0,0,.2), rgba(255,255,255,.2))" />
          )}
          <Anchor
            m="0"
            component={Link}
            to={to}
            key={to}
            tt="uppercase"
            size="xs"
            fw={i === breadcrumbs.length - 1 ? '500' : 'normal'}
          >
            {title}
          </Anchor>
        </Fragment>
      ))}
    </Group>
  );
}

function NavBadge({
  badgeKey,
  counters,
}: {
  badgeKey?: NavCounterKey;
  counters?: TNavCounters;
}) {
  const count = badgeKey ? counters?.[badgeKey] : undefined;

  if (!count) {
    return null;
  }

  return (
    <Badge
      size="sm"
      variant="filled"
      color="red"
      circle
      data-testid={`nav-badge-${badgeKey}`}
    >
      {count}
    </Badge>
  );
}

export default function Layout({ children }: PropsWithChildren) {
  const { user, signOut } = useAuth();
  const [pathname] = useLocation();
  useSyncExternalStore(subscribeListMemory, listMemoryVersion);
  const { permissionManager } = usePermissions();
  const [, nav] = useLocation();
  const { data: navCounters } = useNavCounters();

  // Filter menu items based on user permissions
  const visibleMenuGroups = useMemo(() => {
    const isVisible = (item: MenuItem) => {
      // If no permissions required, item is always visible
      if (!item.permissions || item.permissions.length === 0) {
        return true;
      }

      // Check permissions based on requireAll flag
      if (item.requireAll) {
        return permissionManager.hasAllPermissions(item.permissions);
      } else {
        return permissionManager.hasAnyPermission(item.permissions);
      }
    };

    return menuGroups
      .map((group) => ({ ...group, items: group.items.filter(isVisible) }))
      .filter((group) => group.items.length > 0);
  }, [permissionManager]);

  return (
    <AppShell
      offsetScrollbars={false}
      header={{ height: 60 }}
      navbar={{
        width: 220,
        breakpoint: 'sm',
      }}
      padding={0}
    >
      <AppShell.Header p="md" data-testid="app-header">
        <Flex direction="row" justify="space-between">
          <Group h="100%">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Group>
                <Logo />
                <HeaderText>BACKOFFICE</HeaderText>
              </Group>
            </Link>
            <BreadBar />
          </Group>
          <Group
            display={{ base: 'none', sm: 'flex' }}
            h="100%"
            gap="xs"
            align="center"
          >
            <Text size="sm" data-testid="current-user">
              {user?.info.name}
            </Text>
            <ThemeSwitcher />
            <Button
              leftSection={<SlLogout size={16} />}
              variant="transparent"
              size="sm"
              onClick={() => {
                signOut();
                googleLogout();
                nav('/');
              }}
            >
              Logout
            </Button>
          </Group>
        </Flex>
      </AppShell.Header>
      <AppShell.Navbar data-testid="main-nav">
        <Stack gap={0}>
          {visibleMenuGroups.map((group, gi) => (
            <Fragment key={group.title ?? gi}>
              {group.title && (
                <Text
                  size="xs"
                  c="dimmed"
                  tt="uppercase"
                  fw={600}
                  ta="left"
                  pl={20}
                  pt="md"
                  pb={4}
                >
                  {group.title}
                </Text>
              )}
              {group.items.map((item) => {
                const target = listUrl(item.to);
                return (
                  <NavLink
                    key={item.to}
                    component={Link}
                    label={item.label}
                    to={target}
                    rightSection={
                      <Group gap={6} wrap="nowrap">
                        <NavBadge
                          badgeKey={item.badgeKey}
                          counters={navCounters}
                        />
                        <item.icon />
                      </Group>
                    }
                    pl={20}
                    styles={{
                      body: { flex: 1, minWidth: 0, textAlign: 'left' },
                      label: { whiteSpace: 'nowrap' },
                      section: { flexShrink: 0 },
                    }}
                    {...((pathname === item.to ||
                      pathname.startsWith(`${item.to}/`)) && {
                      active: true,
                    })}
                  />
                );
              })}
            </Fragment>
          ))}
        </Stack>
      </AppShell.Navbar>
      <AppShell.Main>
        {/* // <Box
          //   pos='relative'
          //   h='calc(100vh - var(--app-shell-header-height, 0) - var(--app-shell-padding, 0) * 2)'
          //   p='0'
          // >
          //   <LoadingOverlay zIndex={1000} visible={isLoading} />
          // </Box> */}

        {children}
      </AppShell.Main>
    </AppShell>
  );
}
